import * as React from 'react';
import { Box, Dialog as DialogContainer, useMediaQuery, useTheme } from '@mui/material';

import { Button, Container, ContainerItem, Text } from 'ui/atoms';
import { DialogContent, DialogFooter, DialogHeader } from 'ui/molecules';
import palette from 'ui/theme/palette';

interface Props {
  isOpen: boolean;
  handleClose: (event: React.SyntheticEvent | React.MouseEvent, reason?: string) => void;
  action?: JSX.Element;
  cancelButtonText?: string;
  children?: React.ReactNode;
  className?: string;
  dataCy?: string;
  disableCancelButton?: boolean;
  errorMessage?: string;
  fullScreen?: boolean;
  height?: string;
  hideBackdrop?: boolean;
  hideHeaderClose?: boolean;
  noActions?: boolean;
  noCancelAction?: boolean;
  onCancel?: () => void;
  secondAction?: JSX.Element;
  supportingActions?: JSX.Element;
  sx?: React.CSSProperties;
  title?: string;
  width?: string;
  /** Set to zero for no animation */
  transitionDuration?: number;
}

/**
 * Return dialog/modal component.
 *
 * NOTE: When adding Container and ContainerItem components to the body of a Dialog, the developer must
 * account for the default padding value of 1 these components have by overriding them manually (in almost
 * all cases). Otherwise, the Dialog body will not align with the Dialog header and footer components.
 *
 * @returns a Dialog (Modal) component.
 */
export const Dialog: React.FC<Props> = ({
  isOpen,
  handleClose,
  className,
  action,
  secondAction,
  cancelButtonText,
  dataCy,
  fullScreen = false,
  height = 'auto',
  width = '460px',
  noActions = false,
  onCancel,
  title,
  errorMessage,
  sx,
  supportingActions,
  noCancelAction = false,
  hideHeaderClose = false,
  hideBackdrop = false,
  disableCancelButton = false,
  children,
  transitionDuration
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const calculateFinalWidth = () => {
    if (fullScreen) return '100%';
    if (isMobile) return '360px';
    return width;
  };

  return (
    <DialogContainer
      transitionDuration={transitionDuration}
      hideBackdrop={hideBackdrop}
      data-cy={dataCy}
      fullScreen={fullScreen}
      onClose={handleClose}
      open={isOpen}
      sx={{
        ...sx,
        justifyContent: 'center'
      }}
      PaperProps={{
        style: {
          overflow: 'visible',
          borderRadius: '6px',
          boxShadow: hideBackdrop
            ? 'none'
            : '0px 11px 15px -7px rgba(0,0,0,0.2), 0px 24px 38px 3px rgba(0,0,0,0.14), 0px 9px 46px 8px rgba(0,0,0,0.12)'
        }
      }}
      className={className}
      maxWidth="xl"
    >
      <Box
        sx={{
          width: calculateFinalWidth(),
          height: height,
          overflow: 'auto',
          justifyContent: 'space-between',
          flexDirection: 'column',
          display: 'flex'
        }}
      >
        <Box
          style={{
            overflow: 'visible',
            paddingBottom: '20px',
            marginBottom: noActions ? 0 : '74px'
          }}
        >
          <DialogHeader
            isMobile={isMobile}
            width={width}
            title={title || ''}
            handleClose={handleClose}
            hideClose={hideHeaderClose}
          />

          <DialogContent isMobile={isMobile}>
            <>
              {children}
              {errorMessage && <Text sx={{ color: palette.error.main }} content={errorMessage} />}
            </>
          </DialogContent>
        </Box>

        {!noActions && (
          <DialogFooter>
            <Box
              style={{
                backgroundColor: 'white',
                width: '100%',
                height: '74px',
                zIndex: '10',
                boxShadow: 'rgb(0 0 0 / 10%) 0px -1px 3px',
                borderTop: '1px solid #C6CCDA',
                borderRadius: '0 0 6px 6px',
                position: 'absolute',
                bottom: 0,
                right: 0
              }}
            >
              {noCancelAction ? (
                <Box sx={{ padding: '16px', width: '80%', textAlign: 'center', margin: 'auto' }}>
                  {action && action}
                </Box>
              ) : (
                <Container
                  margin={isMobile ? '24px 10px 17px 10px' : '18px 0px 17px 0px'}
                  padding="0 24px 0 16px"
                  width="auto"
                  align="center"
                  justify={isMobile ? 'space-between' : 'flex-end'}
                >
                  {supportingActions && (
                    <ContainerItem grow dataCy="dialog-support-action" padding="0" margin="0">
                      {supportingActions}
                    </ContainerItem>
                  )}
                  <ContainerItem
                    padding={isMobile ? '0 4px 0 0' : '0'}
                    margin="0"
                    width={
                      isMobile && !secondAction ? '50%' : isMobile && secondAction ? '33%' : 'fit-content'
                    }
                  >
                    <Button
                      useMinWidth
                      label={cancelButtonText ?? 'Cancel'}
                      color="default-outlined"
                      disabled={disableCancelButton}
                      variant="text"
                      onClick={(event: React.SyntheticEvent | React.MouseEvent, reason?: string) => {
                        if (onCancel) {
                          onCancel();
                        }
                        handleClose(event, reason);
                      }}
                      style={{ minWidth: isMobile ? '100%' : '100px' }}
                      dataCy="cancel-button"
                    />
                  </ContainerItem>
                  {action && (
                    <ContainerItem
                      dataCy="dialog-action"
                      padding={isMobile ? '0 0 0 4px' : '0'}
                      margin={isMobile ? '0' : '0 0 0 8px'}
                      width={
                        isMobile && !secondAction ? '50%' : isMobile && secondAction ? '33%' : 'fit-content'
                      }
                    >
                      {action}
                    </ContainerItem>
                  )}
                  {secondAction && (
                    <ContainerItem
                      dataCy="dialog-second-action"
                      padding={isMobile ? '0 0 0 4px' : '0'}
                      margin={isMobile ? '0' : '0 0 0 8px'}
                      width={isMobile ? '33%' : 'fit-content'}
                    >
                      {secondAction}
                    </ContainerItem>
                  )}
                </Container>
              )}
            </Box>
          </DialogFooter>
        )}
      </Box>
    </DialogContainer>
  );
};
